// @ts-strict-ignore
/**
 * FlowsEntryRow is specific to the flows report where forward and reverse steps
 * are represented in the query builder.
 */

/* eslint-disable @typescript-eslint/no-use-before-define */

import React, {useState} from 'react';
import clamp from 'lodash/clamp';

import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {Placement} from 'common/components/tooltip/types';
import {
  Action,
  EntryPart,
  FiltersOperator,
  FlowsQueryEventEntry,
  isEventBasedEntry,
} from 'common/query-builder/query-entry-section/types';
// eslint-disable-next-line import/no-cycle
import {RowFilters} from 'common/query-builder/query-entry-section/components/filters';
import {RowSelector} from 'common/query-builder/query-entry-section/components/selector';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {useTranslation} from 'common/i18n/hooks';

import {BaseEntryRow} from '../base';
import {EntryRowProps} from '../types';
import {getEntryFilters} from '../row-util';
import {NumberOfSteps} from './number-of-steps';
import {hasEventValue} from './utils';
// eslint-disable-next-line import/no-cycle
import {helpers} from './helpers';

export function FlowsEntryRow(props: EntryRowProps<FlowsQueryEventEntry>) {
  const {t} = useTranslation();

  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);

  const handleFilterSectionUpdate = (
    filters: PropertyFilter[],
    filtersOperator: FiltersOperator,
    queryReason: QueryReason,
  ) => {
    props.updateEntry(
      Action.Edit,
      {
        ...props.entry,
        filters,
        filtersOperator,
      },
      {queryReason},
    );
  };

  const handleReverseStepsUpdate = (reverse: number) => {
    reverse = clamp(reverse, 0, 99);
    const delta = Math.abs(props.entry.reverse - reverse);
    const entry = {
      ...props.entry,
      reverse,
    };

    props.updateEntry(Action.EditFlowSteps, entry, {trackingProps: {reverse, delta}});
    return reverse;
  };

  const handleForwardStepsUpdate = (forward: number) => {
    forward = clamp(forward, 0, 99);
    const delta = Math.abs(props.entry.forward - forward);
    const entry = {
      ...props.entry,
      forward,
    };

    props.updateEntry(Action.EditFlowSteps, entry, {trackingProps: {forward, delta}});
    return forward;
  };

  const selectorIcon = props.entry.funnelStep ? `funnel-steps` : null;
  const selectorRenderer = () => (
    <>
      {/** main selector */}
      <RowSelector
        {...props}
        contextPanelParentElement={entryEl}
        isPlaceholder={!hasEventValue(props.entry)}
        selectorIcon={selectorIcon}
      />
    </>
  );

  const suffixRenderer = () => (
    <>
      {/* step controls */}
      <div className='mp-query-entry-row for-flows-steps'>
        <NumberOfSteps
          forward={props.entry.forward ?? 0}
          reverse={props.entry.reverse ?? 0}
          onChangeForward={handleForwardStepsUpdate}
          onChangeReverse={handleReverseStepsUpdate}
        />
      </div>

      {/* inline filters */}
      <RowFilters
        entry={props.entry}
        eventNames={props.entry.event?.value ? [props.entry.event.value] : []}
        filters={getEntryFilters(props.entry, props.sectionProps.behaviorMode)[EntryPart.Filters]}
        index={props.index}
        onUpdate={handleFilterSectionUpdate}
        hideEvents={!isEventBasedEntry(props.entry)}
        hasNewUI={props.hasNewUI}
        sectionProps={props.sectionProps}
        nestedSectionParams={props.nestedSectionParams}
        placeholders={props.placeholder}
        onAbortStage={() => props.setActivePart(EntryPart.Filters, `close`)}
      />
    </>
  );

  // renders a filter button shortcut
  const actionsRenderer =
    props.getFilteringCapabilities().canApplyFilter &&
    (() => (
      <ButtonReact
        theme={ButtonTheme.Tertiary}
        icon='filter-xs'
        tooltip={{
          'delay-in': 10,
          'delay-out': 10,
          placement: Placement.Top,
          text: t(`query-builder.actions.filter`),
        }}
        onClick={() => props.showPlaceholder(EntryPart.Filters)}
      />
    ));

  return (
    <BaseEntryRow
      ref={setEntryEl}
      {...props}
      showDefaultMetricUnitSelector={false}
      renderActions={actionsRenderer}
      renderSelector={selectorRenderer}
      renderSuffix={suffixRenderer}
      isPlaceholder={helpers.isPlaceholder(props)}
      overflowControls={props.getCommonOverflowControls()}
    />
  );
}
